/**
 * DialogShell — the standard popup chrome for Flow editor dialogs.
 *
 * Every Flow popup (Save, Load, New map, Edit info, …) renders through this so
 * they share one format, matching the Flow Settings window:
 *   - a header with a title + optional subtitle on the left and an ✕ close
 *     button on the right (no footer "Cancel" needed — ✕ / Esc / backdrop
 *     dismiss),
 *   - the body content,
 *   - an optional divider-topped footer for the primary action(s).
 *
 * Dismissal (✕ / Esc / backdrop click) is disabled while `busy` is true so an
 * in-flight operation can't be interrupted.
 */

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { color } from '../../components/ui/theme';
import { backdropStyle, dialogStyle, titleStyle, subtitleStyle } from './FlowDialogs.styles';

export function DialogShell({
  title, subtitle, onClose, width = 420, busy = false, footer, children,
}: {
  title: string;
  subtitle?: React.ReactNode;
  onClose: () => void;
  width?: number;
  /** When true, ✕ / Esc / backdrop won't dismiss (an operation is running). */
  busy?: boolean;
  /** Optional footer content, rendered in a divider-topped, right-aligned row. */
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [closeHover, setCloseHover] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  return ReactDOM.createPortal(
    <div style={backdropStyle} onClick={busy ? undefined : onClose}>
      <div
        style={{
          ...dialogStyle,
          // Responsive: never exceed the viewport (small screens scroll inside).
          width: `min(${width}px, calc(100vw - 24px))`,
          maxHeight: 'calc(100vh - 24px)',
          overflowY: 'auto',
          position: 'relative',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={headerStyle}>
          <div style={headerTextStyle}>
            <div style={titleStyle}>{title}</div>
            {subtitle != null && <div style={{ ...subtitleStyle, marginTop: 0 }}>{subtitle}</div>}
          </div>
          <button
            style={{
              ...closeBtnStyle,
              ...(closeHover
                ? { background: 'var(--accent)', borderColor: 'var(--accent)', color: 'oklch(0.14 calc(0.022 * var(--tint-c)) var(--tint-h))' }
                : null),
            }}
            onClick={onClose}
            onMouseDown={e => e.stopPropagation()}
            onMouseEnter={() => setCloseHover(true)}
            onMouseLeave={() => setCloseHover(false)}
            disabled={busy}
            title="Close (Esc)"
          >
            ✕
          </button>
        </div>

        {children}

        {footer && <div style={footerRowStyle}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
};

const headerTextStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  minWidth: 0,
};

const closeBtnStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  // Accent ring + accent X at rest; hover fills with accent (X turns dark tint).
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.55)',
  background: 'transparent',
  color: 'var(--accent)',
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
  flexShrink: 0,
  transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
};

const footerRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  justifyContent: 'flex-end',
  marginTop: 4,
  paddingTop: 12,
  borderTop: '1px solid rgba(120, 80, 220, 0.18)',
};
